import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDataRefresh, ENTITY_TYPES } from '../context/DataRefreshContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Trash2,
  RefreshCw,
  Users,
  FileText,
  ShoppingBag,
  Search,
  AlertTriangle,
  CheckCircle,
  Clock,
  Eye,
  RotateCcw,
  Shield,
  X,
  History
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-CO', { 
    day: '2-digit', 
    month: 'short', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatPrice = (value) => {
  if (!value) return '$0';
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const RegistrosEliminados = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { notifyRestoration } = useDataRefresh();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('clientes');
  const [data, setData] = useState({
    clientes: [],
    cotizaciones: [],
    ventas: []
  });
  const [auditoria, setAuditoria] = useState([]);
  const [showAuditoria, setShowAuditoria] = useState(false);
  
  // Modal de restauración
  const [showRestaurarModal, setShowRestaurarModal] = useState(false);
  const [registroARestaurar, setRegistroARestaurar] = useState(null);
  const [tipoRestaurar, setTipoRestaurar] = useState('');
  const [motivoRestauracion, setMotivoRestauracion] = useState('');
  const [restaurando, setRestaurando] = useState(false);

  // Verificar rol de gerencia
  const esGerencia = user?.role === 'gerencia' || user?.role === 'admin';

  const fetchEliminados = useCallback(async () => {
    if (!esGerencia) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/gerencia/eliminados`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Error cargando eliminados:', error);
      toast.error('Error al cargar registros eliminados');
    } finally {
      setLoading(false);
    }
  }, [esGerencia]);

  const fetchAuditoria = useCallback(async () => {
    if (!esGerencia) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/gerencia/auditoria?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuditoria(response.data.logs || []);
    } catch (error) {
      console.error('Error cargando auditoría:', error);
    }
  }, [esGerencia]);

  useEffect(() => {
    if (esGerencia) {
      fetchEliminados();
      fetchAuditoria();
    }
  }, [esGerencia, fetchEliminados, fetchAuditoria]);

  const abrirModalRestaurar = (registro, tipo) => {
    setRegistroARestaurar(registro);
    setTipoRestaurar(tipo);
    setMotivoRestauracion('');
    setShowRestaurarModal(true);
  };

  const restaurarRegistro = async () => {
    if (!motivoRestauracion.trim() || motivoRestauracion.length < 5) {
      toast.error('El motivo de restauración debe tener al menos 5 caracteres');
      return;
    }

    setRestaurando(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = `/api/gerencia/restaurar/${tipoRestaurar}/${registroARestaurar.id}`;
      
      await axios.post(`${API_URL}${endpoint}`, 
        { motivo_restauracion: motivoRestauracion },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Registro restaurado exitosamente');
      setShowRestaurarModal(false);
      
      // Notificar invalidación de caché según el tipo de registro
      const entityTypeMap = {
        'cliente': ENTITY_TYPES.CLIENTES,
        'cotizacion': ENTITY_TYPES.COTIZACIONES,
        'venta': ENTITY_TYPES.VENTAS
      };
      const entityType = entityTypeMap[tipoRestaurar] || ENTITY_TYPES.ALL;
      notifyRestoration(entityType, { id: registroARestaurar.id });
      
      fetchEliminados();
      fetchAuditoria();
    } catch (error) {
      console.error('Error restaurando:', error);
      toast.error(error.response?.data?.detail || 'Error al restaurar el registro');
    } finally {
      setRestaurando(false);
    }
  };

  const getTotalEliminados = () => {
    return data.clientes.length + data.cotizaciones.length + data.ventas.length;
  };

  if (!esGerencia) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <Shield className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Acceso Restringido</h2>
          <p className="text-slate-600 mb-4">
            Solo usuarios con rol <strong>Gerencia</strong> pueden acceder a esta sección.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-slate-800 text-white px-6 py-2 rounded-lg hover:bg-slate-700 transition"
          >
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-slate-100 rounded-lg transition"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                  <Trash2 className="w-6 h-6 text-red-500" />
                  Registros Eliminados
                </h1>
                <p className="text-sm text-slate-500">Borrado lógico - Solo Gerencia</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowAuditoria(!showAuditoria)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                  showAuditoria 
                    ? 'bg-indigo-100 text-indigo-700' 
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <History className="w-4 h-4" />
                <span className="hidden sm:inline">Auditoría</span>
              </button>
              <button
                onClick={fetchEliminados}
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Actualizar</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Tarjetas de resumen */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-slate-400">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Eliminados</p>
                <p className="text-2xl font-bold text-slate-800">{getTotalEliminados()}</p>
              </div>
              <Trash2 className="w-10 h-10 text-slate-300" />
            </div>
          </div>
          <div 
            className={`bg-white rounded-xl shadow-sm p-5 border-l-4 cursor-pointer transition ${
              activeTab === 'clientes' ? 'border-blue-500 ring-2 ring-blue-100' : 'border-blue-300 hover:border-blue-400'
            }`}
            onClick={() => setActiveTab('clientes')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Clientes</p>
                <p className="text-2xl font-bold text-blue-600">{data.clientes.length}</p>
              </div>
              <Users className="w-10 h-10 text-blue-300" />
            </div>
          </div>
          <div 
            className={`bg-white rounded-xl shadow-sm p-5 border-l-4 cursor-pointer transition ${
              activeTab === 'cotizaciones' ? 'border-amber-500 ring-2 ring-amber-100' : 'border-amber-300 hover:border-amber-400'
            }`}
            onClick={() => setActiveTab('cotizaciones')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Cotizaciones</p>
                <p className="text-2xl font-bold text-amber-600">{data.cotizaciones.length}</p>
              </div>
              <FileText className="w-10 h-10 text-amber-300" />
            </div>
          </div>
          <div 
            className={`bg-white rounded-xl shadow-sm p-5 border-l-4 cursor-pointer transition ${
              activeTab === 'ventas' ? 'border-green-500 ring-2 ring-green-100' : 'border-green-300 hover:border-green-400'
            }`}
            onClick={() => setActiveTab('ventas')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Ventas</p>
                <p className="text-2xl font-bold text-green-600">{data.ventas.length}</p>
              </div>
              <ShoppingBag className="w-10 h-10 text-green-300" />
            </div>
          </div>
        </div>

        {/* Panel de auditoría */}
        {showAuditoria && (
          <div className="bg-white rounded-xl shadow-sm mb-6 overflow-hidden">
            <div className="bg-indigo-50 px-6 py-4 border-b">
              <h3 className="font-semibold text-indigo-800 flex items-center gap-2">
                <History className="w-5 h-5" />
                Historial de Eliminaciones y Restauraciones
              </h3>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {auditoria.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  No hay registros de auditoría
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Fecha</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Acción</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Tipo</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Registro</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Usuario</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Motivo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {auditoria.map((log) => (
                      <tr key={log.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm text-slate-600">{formatDate(log.created_at)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            log.tipo.includes('restauracion') 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {log.tipo.includes('restauracion') ? (
                              <><RotateCcw className="w-3 h-3" /> Restauración</>
                            ) : (
                              <><Trash2 className="w-3 h-3" /> Eliminación</>
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 capitalize">{log.registro_tipo}</td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-slate-800">{log.registro_id_legible || log.registro_nombre}</div>
                          <div className="text-xs text-slate-500">{log.registro_nombre}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{log.usuario_nombre}</td>
                        <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate">{log.motivo}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Contenido según pestaña activa */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Tabs */}
          <div className="border-b flex">
            <button
              onClick={() => setActiveTab('clientes')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition ${
                activeTab === 'clientes'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Users className="w-4 h-4" />
                Clientes ({data.clientes.length})
              </div>
            </button>
            <button
              onClick={() => setActiveTab('cotizaciones')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition ${
                activeTab === 'cotizaciones'
                  ? 'text-amber-600 border-b-2 border-amber-600 bg-amber-50'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <FileText className="w-4 h-4" />
                Cotizaciones ({data.cotizaciones.length})
              </div>
            </button>
            <button
              onClick={() => setActiveTab('ventas')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition ${
                activeTab === 'ventas'
                  ? 'text-green-600 border-b-2 border-green-600 bg-green-50'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <ShoppingBag className="w-4 h-4" />
                Ventas ({data.ventas.length})
              </div>
            </button>
          </div>

          {/* Lista según pestaña */}
          <div className="p-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <RefreshCw className="w-8 h-8 text-slate-300 animate-spin" />
              </div>
            ) : (
              <>
                {/* Clientes */}
                {activeTab === 'clientes' && (
                  <>
                    {data.clientes.length === 0 ? (
                      <div className="text-center py-12">
                        <Users className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                        <p className="text-slate-500">No hay clientes eliminados</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {data.clientes.map((cliente) => (
                          <div key={cliente.id} className="border rounded-lg p-4 hover:bg-slate-50 transition">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3">
                                  <span className="text-xs font-mono bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                    {cliente.id_cliente}
                                  </span>
                                  <h4 className="font-semibold text-slate-800">{cliente.nombre}</h4>
                                </div>
                                <div className="mt-2 text-sm text-slate-500 flex flex-wrap gap-4">
                                  <span>Eliminado: {formatDate(cliente.eliminado_at)}</span>
                                  <span>Por: {cliente.eliminado_por_nombre}</span>
                                </div>
                                <div className="mt-1 text-sm text-red-600 bg-red-50 px-2 py-1 rounded inline-block">
                                  Motivo: {cliente.eliminado_motivo}
                                </div>
                              </div>
                              <button
                                onClick={() => abrirModalRestaurar(cliente, 'cliente')}
                                className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition"
                              >
                                <RotateCcw className="w-4 h-4" />
                                Restaurar
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* Cotizaciones */}
                {activeTab === 'cotizaciones' && (
                  <>
                    {data.cotizaciones.length === 0 ? (
                      <div className="text-center py-12">
                        <FileText className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                        <p className="text-slate-500">No hay cotizaciones eliminadas</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {data.cotizaciones.map((cot) => (
                          <div key={cot.id} className="border rounded-lg p-4 hover:bg-slate-50 transition">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3">
                                  <span className="text-xs font-mono bg-amber-100 text-amber-700 px-2 py-1 rounded">
                                    {cot.numero}
                                  </span>
                                  <h4 className="font-semibold text-slate-800">{cot.cliente_nombre}</h4>
                                  <span className="text-sm font-medium text-slate-600">
                                    {formatPrice(cot.total)}
                                  </span>
                                </div>
                                <div className="mt-2 text-sm text-slate-500 flex flex-wrap gap-4">
                                  <span>Eliminada: {formatDate(cot.eliminado_at)}</span>
                                  <span>Por: {cot.eliminado_por_nombre}</span>
                                </div>
                                <div className="mt-1 text-sm text-red-600 bg-red-50 px-2 py-1 rounded inline-block">
                                  Motivo: {cot.eliminado_motivo}
                                </div>
                              </div>
                              <button
                                onClick={() => abrirModalRestaurar(cot, 'cotizacion')}
                                className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition"
                              >
                                <RotateCcw className="w-4 h-4" />
                                Restaurar
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* Ventas */}
                {activeTab === 'ventas' && (
                  <>
                    {data.ventas.length === 0 ? (
                      <div className="text-center py-12">
                        <ShoppingBag className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                        <p className="text-slate-500">No hay ventas eliminadas</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {data.ventas.map((venta) => (
                          <div key={venta.id} className="border rounded-lg p-4 hover:bg-slate-50 transition">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3">
                                  <span className="text-xs font-mono bg-green-100 text-green-700 px-2 py-1 rounded">
                                    {venta.numero}
                                  </span>
                                  <h4 className="font-semibold text-slate-800">{venta.cliente_nombre}</h4>
                                  <span className="text-sm font-medium text-green-600">
                                    {formatPrice(venta.total_final)}
                                  </span>
                                </div>
                                <div className="mt-2 text-sm text-slate-500 flex flex-wrap gap-4">
                                  <span>Eliminada: {formatDate(venta.eliminado_at)}</span>
                                  <span>Por: {venta.eliminado_por_nombre}</span>
                                </div>
                                <div className="mt-1 text-sm text-red-600 bg-red-50 px-2 py-1 rounded inline-block">
                                  Motivo: {venta.eliminado_motivo}
                                </div>
                              </div>
                              <button
                                onClick={() => abrirModalRestaurar(venta, 'venta')}
                                className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition"
                              >
                                <RotateCcw className="w-4 h-4" />
                                Restaurar
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Info de ayuda */}
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium mb-1">Acerca del Borrado Lógico</p>
              <ul className="list-disc list-inside space-y-1 text-amber-700">
                <li>Los registros eliminados no se borran permanentemente de la base de datos</li>
                <li>Pueden ser restaurados en cualquier momento por usuarios con rol Gerencia</li>
                <li>Al eliminar un cliente, sus cotizaciones sin venta también se eliminan</li>
                <li>Al restaurar un cliente, sus cotizaciones asociadas también se restauran</li>
                <li>Las ventas preservan los pagos verificados y revierten puntos JPM canjeados</li>
              </ul>
            </div>
          </div>
        </div>
      </main>

      {/* Modal de Restauración */}
      {showRestaurarModal && registroARestaurar && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <RotateCcw className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-800">Restaurar Registro</h3>
                  <p className="text-sm text-slate-500">
                    {tipoRestaurar === 'cliente' && 'Cliente'}
                    {tipoRestaurar === 'cotizacion' && 'Cotización'}
                    {tipoRestaurar === 'venta' && 'Venta'}
                  </p>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-slate-600">
                  <strong>
                    {tipoRestaurar === 'cliente' && registroARestaurar.id_cliente}
                    {tipoRestaurar === 'cotizacion' && registroARestaurar.numero}
                    {tipoRestaurar === 'venta' && registroARestaurar.numero}
                  </strong>
                  {' - '}
                  {tipoRestaurar === 'cliente' && registroARestaurar.nombre}
                  {tipoRestaurar === 'cotizacion' && registroARestaurar.cliente_nombre}
                  {tipoRestaurar === 'venta' && registroARestaurar.cliente_nombre}
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Motivo de la restauración *
                </label>
                <textarea
                  value={motivoRestauracion}
                  onChange={(e) => setMotivoRestauracion(e.target.value)}
                  placeholder="Explique por qué se está restaurando este registro..."
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  rows={3}
                />
                <p className="text-xs text-slate-500 mt-1">Mínimo 5 caracteres</p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowRestaurarModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition"
                  disabled={restaurando}
                >
                  Cancelar
                </button>
                <button
                  onClick={restaurarRegistro}
                  disabled={restaurando || motivoRestauracion.length < 5}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {restaurando ? (
                    <><RefreshCw className="w-4 h-4 animate-spin" /> Restaurando...</>
                  ) : (
                    <><CheckCircle className="w-4 h-4" /> Confirmar</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrosEliminados;
